import { useCallback, useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react'
import { useAuth } from '../context/AuthContext'
import { useAccessContext } from '../context/AccessContext'
import { createTableColumnPreferenceStore, tableColumnPreferenceKey } from '../services/interfacePreferences'
import type { TableColumn, TablePreferenceScope } from '../services/tableState'

export function useTableColumnPreference(scope: TablePreferenceScope | null, columns: readonly TableColumn[], contextRevision = '') {
  const key = tableColumnPreferenceKey(scope)
  const store = useMemo(() => createTableColumnPreferenceStore(key, columns), [key, columns, contextRevision])
  const current = useRef<typeof store | null>(store)
  current.current = store
  useLayoutEffect(() => {
    current.current = store
    return () => { if (current.current === store) current.current = null }
  }, [store])
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)
  const setVisible = useCallback((id: string, visible: boolean) => {
    if (current.current === store) store.setVisible(id, visible)
  }, [store])
  const restoreDefaults = useCallback(() => {
    if (current.current === store) store.restoreDefaults()
  }, [store])
  const setHiddenColumnIds = useCallback((ids: readonly string[]) => {
    if (current.current === store) store.setHiddenColumnIds(ids)
  }, [store])
  return { ...snapshot, setHiddenColumnIds, enabled: key !== null, setVisible, restoreDefaults,
    isVisible: (id: string) => columns.some(column => column.id === id && (column.required || !snapshot.hiddenColumnIds.includes(id))),
    visibleCount: columns.filter(column => column.required || !snapshot.hiddenColumnIds.includes(column.id)).length }
}
export type TableColumnPreference = ReturnType<typeof useTableColumnPreference>

/** Verified workforce adapter; authorization remains in existing AccessProvider/domain services. */
export function useWorkspaceTableColumns(workspaceId: string, tableId: string, columns: readonly TableColumn[]) {
  const { user, loading: authLoading } = useAuth()
  const { accessContext, selectedTenantId, currentOrganization, loading, error, authenticationRequired } = useAccessContext()
  const tenantId = accessContext?.tenantId
  const ready = !authLoading && !loading && !error && !authenticationRequired && Boolean(user?.id
    && accessContext?.currentUser?.id === user.id && tenantId && (!selectedTenantId || selectedTenantId === tenantId))
  const contextRevision = useMemo(() => ready ? JSON.stringify(accessContext) : '', [ready, accessContext])
  // Retire old setters after any access/entitlement change, including A → B → A.
  const scope = useMemo(() => ready ? { userId: user!.id, tenantId: tenantId!, workspaceId,
    organizationId: currentOrganization?.id || null, tableId } : null, [ready,user?.id,tenantId,workspaceId,currentOrganization?.id,tableId])
  return { ...useTableColumnPreference(scope, columns, contextRevision), scope, contextRevision }
}
